import { createContext, useContext, type ReactNode } from "react";
import { Chip } from "@/components/ui/chip";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { useProjectRole } from "@/lib/use-project-role";

export type ProjectAccess = {
  role: string | null;
  /** May write to the project (owner or editor). */
  canEdit: boolean;
  isOwner: boolean;
  /** Signed in as a project viewer — show "ask an editor" copy. */
  isViewer: boolean;
};

/**
 * Outside a project (team-level pages) nothing is project-gated, so the
 * default grants everything.
 */
const UNSCOPED: ProjectAccess = {
  role: null,
  canEdit: true,
  isOwner: true,
  isViewer: false,
};

const ProjectAccessContext = createContext<ProjectAccess>(UNSCOPED);

export function toProjectAccess(r: {
  role: string | null;
  canEdit: boolean;
  isOwner: boolean;
}): ProjectAccess {
  return { ...r, isViewer: r.role === "viewer" };
}

/** Resolves the signed-in user's role once for everything under a project. */
export function ProjectAccessProvider({
  projectId,
  children,
}: {
  projectId: string;
  children: ReactNode;
}) {
  const role = useProjectRole(projectId);
  return (
    <ProjectAccessContext.Provider value={toProjectAccess(role)}>
      {children}
    </ProjectAccessContext.Provider>
  );
}

/** Test/story seam: provide a fixed access value. */
export const ProjectAccessValueProvider = ProjectAccessContext.Provider;

export function useProjectAccess(): ProjectAccess {
  return useContext(ProjectAccessContext);
}

/** True when the user may write to the current project. */
export function useCanEdit(): boolean {
  return useProjectAccess().canEdit;
}

/**
 * Renders its children only for users who may write to the project. The
 * API refuses viewer writes, so viewers don't see the actions at all.
 */
export function CanEdit({
  children,
  fallback = null,
}: {
  children: ReactNode;
  fallback?: ReactNode;
}) {
  return <>{useCanEdit() ? children : fallback}</>;
}

/**
 * Copy for empty states: the editor instruction, or who to ask when the
 * reader can only view.
 */
export function useEditorCopy(forEditor: string, forViewer: string): string {
  return useProjectAccess().isViewer ? forViewer : forEditor;
}

/** Empty-state line for surfaces that fill up once a walkthrough runs. */
export function useFirstRunCopy(): string {
  return useEditorCopy(
    "Run a scenario from your agent with /iamu:run, then sync.",
    "Nothing has run yet. Ask an editor to run a scenario.",
  );
}

/** Quiet marker telling viewers why write actions are missing. */
export function ViewOnlyChip({ className }: { className?: string }) {
  if (!useProjectAccess().isViewer) return null;
  return (
    <SimpleTooltip content="You can view this project. Ask an owner for editor access to make changes.">
      <Chip
        tone="muted"
        tabIndex={0}
        className={cn(
          className,
          "cursor-help focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        )}
      >
        View only
      </Chip>
    </SimpleTooltip>
  );
}
